import {
  Plus,
  ChevronDown,
  SearchX,
  ArrowDownUp,
  ArrowDown,
  ArrowUp,
  EllipsisVertical,
  Trash2,
  Tag,
} from 'lucide-react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from '@tanstack/react-table';
import { Fragment, useMemo, useState, useTransition } from 'react';
import dayjs from 'dayjs';

import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card';
import { EmptyBlock } from '@/components/ui/empty-block';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { AddDepositContextProvider, useAddDeposit } from 'src/context/deposit';
import { useSoftwareEscrow } from 'src/context/software-escrow';
import {
  AddDepositContainer,
  AssetsFilters,
  AssetTypeIcon,
  RemoveAssetDialog,
  AssetAlert,
  useAssetsContext,
  depositStatusMap,
  syncStatusMap,
  refetchAssets,
} from 'src/containers/Deposits';
import { AssetTypeEnum, DepositStatusEnum, EscrowPartyTypeEnum, SyncStatusEnum } from 'src/graphql/schema';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { PROVIDER_KEY_TO_NAME } from 'src/shared/constants';
import { formatBytes } from 'src/utils/formatBytes';
import { Badge } from '@/components/ui/badge';
import { cn } from 'src/lib/utils';
import { SimplePagination } from '@/components/ui/simple-pagination';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { SuggestedChangesBlock, EmptyAssetsCard, RelabelDialog } from './';

type Props = {
  title: string;
  description: string;
  ref: React.RefObject<HTMLDivElement | null>;
};

type IAssetRow = {
  id: string;
  asset: {
    depositSource: string;
    provider?: string;
    type: AssetTypeEnum;
  };
  size: string;
  date: {
    latestDeposit: string;
    lastUpdateCheck: string;
  };
  depositStatus: DepositStatusEnum;
  syncStatus?: SyncStatusEnum | null;
  alert: {
    syncStatus?: SyncStatusEnum | null;
    depositStatus: DepositStatusEnum;
    error?: string;
  };
  actions: {
    id: string;
    canRemove: boolean;
    canUpdate: boolean;
  };
};

const ActionsDropdown = ({ assetId = '', canRemove = false, canUpdate = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { openRemoveDialog, openRelabelDialog } = useAssetsContext();

  if (!canRemove && !canUpdate) return null;

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="data-[state=open]:bg-muted text-muted-foreground flex size-8" size="icon">
          <EllipsisVertical />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-32">
        {canUpdate && (
          <DropdownMenuItem onSelect={() => openRelabelDialog([assetId])}>
            <Tag />
            Relabel
          </DropdownMenuItem>
        )}
        {canRemove && (
          <DropdownMenuItem variant="destructive" onSelect={() => openRemoveDialog(assetId)}>
            <Trash2 />
            Remove
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const RemoveAssetButton = ({ assetId }: { assetId: string }) => {
  const { openRemoveDialog } = useAssetsContext();

  return (
    <Button
      variant="ghost"
      size="icon"
      className="data-[state=open]:bg-muted flex size-8"
      onClick={() => openRemoveDialog(assetId)}
    >
      <Trash2 className="text-destructive" />
    </Button>
  );
};

const columnHelper = createColumnHelper<IAssetRow>();
const columns = [
  columnHelper.accessor('asset', {
    header: (context) => `Assets (${context.table.getRowModel().rows.length})`,
    cell: (props) => {
      const { depositSource, provider, type } = props.getValue();

      const providerName = provider ? PROVIDER_KEY_TO_NAME[provider as keyof typeof PROVIDER_KEY_TO_NAME] : '';

      return (
        <div className="flex items-center gap-2">
          <AssetTypeIcon type={type} provider={provider} />
          <div className="grow min-w-0">
            <div className="truncate" title={depositSource}>
              {depositSource}
            </div>
            {providerName && <div className="text-xs text-muted-foreground">{providerName}</div>}
          </div>
        </div>
      );
    },
    sortingFn: (rowA, rowB) => {
      const valueA = rowA.original.asset.depositSource;
      const valueB = rowB.original.asset.depositSource;

      return valueA.localeCompare(valueB);
    },
    meta: {
      headerClassName: 'max-w-[352px]',
      cellClassName: 'max-w-[352px]',
    },
  }),
  columnHelper.accessor('size', {
    header: 'Size',
    cell: (props) =>
      props.getValue() ? <div className="whitespace-nowrap">{formatBytes(Number(props.getValue()))}</div> : null,
    enableSorting: false,
  }),
  columnHelper.accessor('date', {
    header: 'Last deposit',
    cell: (props) => {
      const { latestDeposit, lastUpdateCheck } = props.getValue();

      if (!latestDeposit) return null;

      const latestDepositDate = dayjs(latestDeposit).format('MMM DD, YYYY');
      const isLastCheckOverWeekAgo = lastUpdateCheck
        ? dayjs(lastUpdateCheck).isBefore(dayjs().subtract(1, 'week'))
        : false;
      const lastUpdateCheckDate = isLastCheckOverWeekAgo
        ? dayjs(latestDeposit).format('MMM DD, YYYY')
        : dayjs(lastUpdateCheck).fromNow();

      return (
        <Fragment>
          <div className="text-sm font-medium">{latestDepositDate}</div>
          {lastUpdateCheckDate && (
            <div className="text-[10px] text-muted-foreground whitespace-nowrap">Checked {lastUpdateCheckDate}</div>
          )}
        </Fragment>
      );
    },
    sortingFn: (rowA, rowB) => {
      const valueA = rowA.original.date.latestDeposit ? dayjs(rowA.original.date.latestDeposit).unix() : 0;
      const valueB = rowB.original.date.latestDeposit ? dayjs(rowB.original.date.latestDeposit).unix() : 0;

      return valueA - valueB;
    },
  }),
  columnHelper.accessor('syncStatus', {
    header: 'Sync status',
    cell: (props) => {
      const status = props.getValue();

      if (!status) return 'N/A';

      const syncStatus = syncStatusMap[status];

      if (!syncStatus) return 'N/A';

      const { label, textColor, dotColor } = syncStatus;

      return (
        <Badge variant="ghost" className={cn('gap-1 px-0', textColor)}>
          <span className={cn('rounded-full size-1.5', dotColor)} />
          {label}
        </Badge>
      );
    },
    sortingFn: 'text',
  }),
  columnHelper.accessor('depositStatus', {
    header: 'Deposit status',
    cell: (props) => {
      const { label, variant } = depositStatusMap[props.getValue()];

      return <Badge variant={variant}>{label}</Badge>;
    },
    sortingFn: 'text',
  }),
  columnHelper.accessor('alert', {
    header: '',
    cell: (props) => <AssetAlert {...props.getValue()} />,
    enableSorting: false,
    meta: {
      headerClassName: 'w-7',
      cellClassName: 'w-7',
    },
  }),
  columnHelper.accessor('actions', {
    header: '',
    cell: (props) => {
      const { id, canRemove, canUpdate } = props.getValue();
      const { depositStatus } = props.row.original;

      const isErrorStatus = depositStatus === DepositStatusEnum.Error;

      if (isErrorStatus && canRemove) return <RemoveAssetButton assetId={id} />;

      return <ActionsDropdown assetId={id} canRemove={canRemove} canUpdate={canUpdate} />;
    },
    enableSorting: false,
    meta: {
      headerClassName: 'w-16',
      cellClassName: 'w-16',
    },
  }),
];

const AssetsActions = () => {
  const {
    softwareEscrow: { id, policy },
  } = useSoftwareEscrow();
  const {
    ui: { selectEscrow },
  } = useAddDeposit();

  const toggleModal = () => selectEscrow(id);

  return (
    <div className="flex gap-2 justify-between">
      <AssetsFilters />

      {policy.depositAssets && (
        <Button onClick={toggleModal}>
          <Plus />
          Add assets
        </Button>
      )}
    </div>
  );
};

const AssetsTable = () => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [isPending, startTransition] = useTransition();
  const {
    softwareEscrow: { currentCompanyRole },
  } = useSoftwareEscrow();
  const {
    assets,
    totalFilteredPages,
    totalFilteredItems,
    filters: { page, perPage },
    setPage,
  } = useAssetsContext();

  const tableData = useMemo(
    () =>
      assets.map((node) => ({
        id: node.id,
        asset: {
          depositSource: node.depositSource || '',
          provider: node.integration?.provider || '',
          type: node.type,
        },
        provider: node.integration?.provider || '',
        size: node.size || '',
        date: {
          latestDeposit: node.latestDeposit || '',
          lastUpdateCheck: node.lastUpdateCheck || '',
        },
        depositStatus: node.depositStatus || '',
        syncStatus: node.syncStatus,
        alert: {
          depositStatus: node.depositStatus,
          syncStatus: node.syncStatus,
          error: node.error ?? undefined,
        },
        actions: {
          id: node.id,
          canRemove: node.policy.remove,
          canUpdate: node.policy.update,
        },
      })),
    [assets],
  );

  const table = useReactTable({
    data: tableData,
    columns:
      currentCompanyRole === EscrowPartyTypeEnum.Beneficiary
        ? columns.filter(({ accessorKey }) => accessorKey !== 'actions')
        : columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  });

  const onPageChange = (page: number) => startTransition(() => setPage(page));
  const onPrevious = () => onPageChange((page || 1) - 1);
  const onNext = () => onPageChange((page || 1) + 1);

  return (
    <div className="space-y-6 pt-6">
      <AssetsActions />
      <div className="border border-border rounded-md">
        <Table isLoading={isPending}>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className={cn(
                      'bg-gray-50 text-xs text-muted-foreground font-bold whitespace-nowrap first:rounded-tl-md last:rounded-tr-md',
                      header.column.columnDef.meta?.headerClassName,
                    )}
                  >
                    {header.column.getCanSort() ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={header.column.getToggleSortingHandler()}
                        className="-ml-3 text-muted-foreground! text-xs! font-bold"
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {{
                          asc: <ArrowUp />,
                          desc: <ArrowDown />,
                        }[header.column.getIsSorted() as string] ?? <ArrowDownUp />}
                      </Button>
                    ) : (
                      flexRender(header.column.columnDef.header, header.getContext())
                    )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} className="group/row">
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={cn(
                        'group-last/row:first:rounded-bl-md group-last/row:last:rounded-br-md',
                        cell.column.columnDef.meta?.cellClassName,
                      )}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={table.getAllColumns().length} className="h-4 text-center">
                  <EmptyBlock
                    title="No results"
                    icon={SearchX}
                    withCard={false}
                    classes={{
                      title: 'text-sm',
                    }}
                  />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <SimplePagination
        page={page || 1}
        totalPages={totalFilteredPages}
        totalItems={totalFilteredItems}
        limit={perPage || 10}
        onPrevious={onPrevious}
        onNext={onNext}
        isPreviousDisabled={(page || 1) <= 1}
        isNextDisabled={(page || 1) >= totalFilteredPages}
        hideOnSinglePage
        className="justify-end"
      />
    </div>
  );
};

const CollapsibleCard = ({ title = '', description = '' }) => {
  const [isOpen, setIsOpen] = useState(true);
  const { totalAssetsCount, latestDeposit, contentType } = useAssetsContext();

  const infoItems = [
    {
      label: 'Total number of assets',
      value: totalAssetsCount,
    },
    {
      label: 'Last updated',
      value: latestDeposit ? dayjs(latestDeposit).format('MMM DD, YYYY') : '-',
    },
  ];

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} asChild>
      <Card>
        <CardHeader className="flex-row justify-between items-center gap-2 space-y-0 [&:has(+[data-state=open])]:pb-0">
          <div className="text-base font-semibold">{title}</div>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="text-xs">
              {isOpen ? 'Hide' : 'Show'} assets
              <ChevronDown className={cn('transition-transform rotate-0', isOpen && 'rotate-180')} />
            </Button>
          </CollapsibleTrigger>
        </CardHeader>
        <CollapsibleContent className="pb-0">
          <CardContent className="space-y-6">
            <CardDescription className="mb-6">{description}</CardDescription>
            <SuggestedChangesBlock contentType={contentType} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {infoItems.map(({ label, value }) => (
                <div key={label} className="bg-background-secondary border border-border rounded-lg p-5 space-y-1">
                  <div className="text-lg font-bold text-foreground">{value}</div>
                  <div className="text-xs text-muted-foreground">{label}</div>
                </div>
              ))}
            </div>
            <AssetsTable />
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};

const AssetsBlock = ({ title, description, ref }: Props) => {
  const {
    softwareEscrow: { storageRegion },
  } = useSoftwareEscrow();
  const { contentType, totalAssetsCount } = useAssetsContext();

  return (
    <AddDepositContextProvider refetch={refetchAssets} contentType={contentType} storageRegion={storageRegion}>
      <section ref={ref}>
        {totalAssetsCount ? (
          <CollapsibleCard title={title} description={description} />
        ) : (
          <EmptyAssetsCard title={title} description={description} />
        )}
      </section>
      <AddDepositContainer />
      <RemoveAssetDialog />
      <RelabelDialog />
    </AddDepositContextProvider>
  );
};

export default AssetsBlock;
